import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Stethoscope, User, Video, Pill, FlaskConical, Brain, ClipboardPlus, Activity, Scan, Microscope, Dna, Camera, Eye, Bone, Atom, Syringe, Cpu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ICONS = {
  "user-round": User, stethoscope: Stethoscope, video: Video, pill: Pill,
  "flask-conical": FlaskConical, brain: Brain, "clipboard-plus": ClipboardPlus, activity: Activity,
  scan: Scan, microscope: Microscope, dna: Dna, camera: Camera, eye: Eye,
  bone: Bone, atom: Atom, syringe: Syringe, cpu: Cpu,
};

export default function Templates({ onOpenAuth }) {
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    http.get("/templates").then((r) => setTemplates(r.data.templates));
  }, []);

  const cats = ["All", ...Array.from(new Set(templates.map((t) => t.category)))];
  const filtered = templates.filter((t) =>
    (cat === "All" || t.category === cat) &&
    (t.title.toLowerCase().includes(q.toLowerCase()) || t.description.toLowerCase().includes(q.toLowerCase()))
  );

  const handleUseTemplate = async (t) => {
    if (!user) { onOpenAuth("register"); return; }
    try {
      const r = await http.post("/projects", {
        title: t.title, description: t.description,
        template_id: t.id, model_id: "openai:gpt-5.6-terra",
      });
      toast.success(`Created project · ${t.title}`);
      nav(`/build/${r.data.id}?prompt=${encodeURIComponent(t.prompt)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create project");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="templates-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Template gallery</p>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Fork a healthcare pattern. Ship faster.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Curated scaffolds for the workflows clinicians and developers ask for most.
          </p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="input-template-search"
            placeholder="Search templates..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="template-category-filters">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.replace(/\s+/g,'-').toLowerCase()}`}
            onClick={() => setCat(c)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              cat === c
                ? "bg-emerald-900 text-white border-emerald-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"
            }`}
          >{c}</button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="template-gallery-grid">
        {filtered.map((t) => {
          const Icon = ICONS[t.icon] || Stethoscope;
          return (
            <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-400 hover:shadow-md transition-all group fade-in" data-testid={`template-card-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-900 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[11px] font-mono uppercase text-emerald-700">{t.category}</p>
                  <h3 className="font-display font-semibold text-lg text-slate-900">{t.title}</h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">{t.description}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {t.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="bg-slate-100 text-slate-700 font-normal">{tag}</Badge>
                ))}
              </div>
              <Button
                className="mt-5 w-full bg-emerald-900 hover:bg-emerald-800 text-white"
                onClick={() => handleUseTemplate(t)}
                data-testid={`btn-use-${t.id}`}
              >
                Use this template
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
